import { type Page, toListQuery } from '@hello-aria/makalu-api-client'
import { QueryStringInput } from '@hello-aria/query-string'
import { isNil } from 'lodash'
import {
    CreateInvoice,
    Invoice,
    InvoiceGateway,
    LinkReceivable,
    LinkReceivablePayload,
    UnlinkReceivable,
    UnlinkReceivablePayload,
    UpdateInvoice,
    toCreateInvoiceBody,
    toUpdateInvoiceBody
} from '../models/invoiceModels'
import { batchFetch, requirePathParam } from '../utils/requestsUtils'
import { requestService } from './requestServices'
import { makaluApi } from '../utils/makaluApiClient'
import { downloadAttachments, uploadFiles } from './uploadServices'
import { trackEvent } from '../utils/analyticsUtils'
import { unpaidLoansStatus } from '../models/loanModels'

interface GetInvoices {
    companyId: string
    hasAttachments: boolean
    query?: QueryStringInput
    signal?: AbortSignal
}

export const listInvoices = batchFetch(async (params: GetInvoices): Promise<Page<Invoice>> => {
    const response = await makaluApi.listPage('/companies/{companyId}/invoices', {
        params: {
            path: { companyId: params.companyId },
            query: toListQuery({
                ...params.query,
                params: {
                    ...params.query?.params,
                    include: 'loans'
                }
            })
        },
        signal: params.signal
    })

    const mappedInvoices = await Promise.all(
        response.body.map((invoice) => resolveInvoice(invoice, params.hasAttachments))
    )
    const sanitizedMappedInvoices = mappedInvoices.filter((invoice): invoice is Invoice => !isNil(invoice))
    return { ...response, body: sanitizedMappedInvoices }
})

interface GetInvoiceById {
    companyId: string
    invoiceId: string | null | undefined
    hasAttachments: boolean
}

export const getInvoice = async (params: GetInvoiceById): Promise<Invoice | undefined> => {
    if (isNil(params.invoiceId)) return undefined

    const { companyId, invoiceId, hasAttachments } = params
    const response = await makaluApi.GET('/companies/{companyId}/invoices/{invoiceId}', {
        params: { path: { companyId, invoiceId }, query: { include: 'loans' } }
    })
    const mappedInvoice = await resolveInvoice(response, hasAttachments)

    return mappedInvoice
}

export const createInvoice = async (companyId: string, payload: CreateInvoice): Promise<Invoice | undefined> => {
    const files = await uploadFiles(payload.attachments, companyId)

    const formattedData = toCreateInvoiceBody(payload, files)
    const response = await makaluApi.POST('/companies/{companyId}/invoices', {
        params: { path: { companyId } },
        body: formattedData
    })
    const mappedInvoice = await resolveInvoice(response)

    trackEvent('Create_Invoice')

    return mappedInvoice
}

export const updateInvoice = async (companyId: string, payload: UpdateInvoice): Promise<Invoice | undefined> => {
    const { invoiceId } = payload

    const files = await uploadFiles(payload.attachments, companyId)

    const formattedData = toUpdateInvoiceBody(payload, files)
    const response = await makaluApi.PATCH('/companies/{companyId}/invoices/{invoiceId}', {
        params: { path: { companyId, invoiceId: requirePathParam('invoiceId', invoiceId) } },
        body: formattedData
    })

    const mappedInvoice = await resolveInvoice(response, false)

    trackEvent('Update_Invoice')

    return mappedInvoice
}

export const unlinkReceivableFromInvoice = async (
    companyId: string,
    payload: UnlinkReceivable
): Promise<Invoice | undefined> => {
    const { invoiceId } = payload

    const formattedData: UnlinkReceivablePayload = {
        receivableIds: payload.currentReceivableIds.filter((id) => id !== payload.receivableIdToUnlink)
    }

    const response = await makaluApi.PATCH('/companies/{companyId}/invoices/{invoiceId}', {
        params: { path: { companyId, invoiceId: requirePathParam('invoiceId', invoiceId) } },
        body: formattedData
    })

    const mappedInvoice = await resolveInvoice(response, false)

    trackEvent('Update_Invoice')

    return mappedInvoice
}

export const linkReceivableToInvoice = async (
    companyId: string,
    payload: LinkReceivable
): Promise<Invoice | undefined> => {
    const { invoiceId } = payload
    const formattedData: LinkReceivablePayload = {
        receivableIds: [...payload.currentReceivableIds, ...(payload.receivableIdsToLink ?? [])]
    }
    const response = await makaluApi.PATCH('/companies/{companyId}/invoices/{invoiceId}', {
        params: { path: { companyId, invoiceId: requirePathParam('invoiceId', invoiceId) } },
        body: formattedData
    })
    const mappedInvoice = await resolveInvoice(response, false)

    trackEvent('Update_Invoice')

    return mappedInvoice
}

export const resolveInvoice = async (
    data: InvoiceGateway | undefined,
    hasAttachments = true
): Promise<Invoice | undefined> => {
    try {
        if (isNil(data)) return undefined

        const attachmentsCount = data?.attachments?.length ?? 0

        return {
            ...data,
            isEditable: data?.loans?.every((loan) => unpaidLoansStatus.includes(loan.status)) ?? true,
            attachments: hasAttachments ? await downloadAttachments(data.attachments) : undefined,
            attachmentCount: attachmentsCount
        }
    } catch (error) {
        if (error instanceof Error) {
            console.error(error.message)
        }

        console.error(error)
    }
}

export const fetchInvoiceFile = async (url: string): Promise<File | undefined> => {
    try {
        const response = await requestService<Blob>(url, {
            headers: { 'Access-Control-Expose-Headers': 'Content-Disposition' },
            responseType: 'blob'
        })

        const header = response?.headers?.['content-disposition']
        const parts = header?.split(';')
        const filename = parts?.[1]?.split('=')[1]

        const blob = response?.body as Blob
        const file = new File([blob], filename ?? '', { type: blob.type })

        return file
    } catch (error) {
        console.error('Error fetching files:', error)
    }
}
